"use client";

import { createContext, type ReactNode, useContext } from "react";

const PortalContainerContext = createContext<HTMLElement | null>(null);

export interface PortalContainerProviderProps {
  /**
   * Element that overlays render into. `null` keeps the primitives' default of
   * `document.body`.
   */
  container: HTMLElement | null;
  children: ReactNode;
}

/**
 * Redirects the overlays of the primitives in this folder (dialog, sheet,
 * popover, select, dropdown menu, tooltip, hover card) into a specific element
 * rather than `document.body`.
 *
 * The chrome extension needs this: its UI lives in a content script's shadow
 * root, so a `document.body` portal escapes into the host EHR page — losing the
 * shadow root's styles, ignoring the panel's bounds, and leaving our nodes in a
 * customer's DOM. Providing an element inside the panel keeps overlays where
 * the user opened them, and because the panel establishes a containing block,
 * the primitives' `fixed` positioning resolves against the panel too.
 */
export function PortalContainerProvider({
  container,
  children,
}: PortalContainerProviderProps) {
  return (
    <PortalContainerContext.Provider value={container}>
      {children}
    </PortalContainerContext.Provider>
  );
}

/**
 * Container that overlays should portal into, or `undefined` when no provider
 * is present so the primitives keep their `document.body` default.
 */
export function usePortalContainer(): HTMLElement | undefined {
  return useContext(PortalContainerContext) ?? undefined;
}
